'use client';

import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { t, tn } from '@/data/translations';

interface VerifyModalProps {
  isOpen: boolean;
  voteCount: number;
  onSubmit: (email: string) => void;
  onSkip: () => void;
  langCode: string;
}

export default function VerifyModal({ isOpen, voteCount, onSubmit, onSkip, langCode }: VerifyModalProps) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.includes('@')) {
      onSubmit(email);
      setSent(true);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/70 z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          <motion.div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-slate-900 rounded-3xl border border-slate-700/50 w-full max-w-sm p-6"
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 100, opacity: 0 }}
              transition={{ type: 'spring', damping: 25 }}
            >
              {!sent ? (
                <>
                  <div className="text-center mb-5">
                    <div className="text-4xl mb-3">
                      {voteCount >= 5 ? '🎉' : '👋'}
                    </div>
                    <h2 className="text-white font-bold text-xl mb-2" lang={langCode}>
                      {tn('verifyTitle', voteCount, langCode)}
                    </h2>
                    <p className="text-slate-400 text-sm" lang={langCode}>
                      {t('verifySubtitle', langCode)}
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-3">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="w-full bg-slate-800 text-white rounded-2xl px-4 py-3 border border-slate-700 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 outline-none"
                      autoFocus
                    />
                    <button
                      type="submit"
                      disabled={!email.includes('@')}
                      className="w-full py-3 rounded-2xl bg-sky-500 text-white font-bold shadow-lg shadow-sky-500/25 disabled:opacity-40 disabled:shadow-none hover:bg-sky-400 transition-colors"
                      lang={langCode}
                    >
                      {t('sendMagicLink', langCode)}
                    </button>
                  </form>

                  <button
                    onClick={onSkip}
                    className="w-full text-center text-slate-500 text-sm mt-3 hover:text-slate-300 transition-colors"
                    lang={langCode}
                  >
                    {t('skipForNow', langCode)}
                  </button>
                </>
              ) : (
                <div className="text-center">
                  <div className="text-4xl mb-3">📬</div>
                  <h2 className="text-white font-bold text-xl mb-2" lang={langCode}>{t('checkEmail', langCode)}</h2>
                  <p className="text-slate-400 text-sm mb-4" lang={langCode}>
                    {t('sentLinkTo', langCode)} <span className="text-white" dir="ltr">{email}</span>
                  </p>
                  <button
                    onClick={onSkip}
                    className="text-sky-400 text-sm hover:text-sky-300 transition-colors"
                    lang={langCode}
                  >
                    {t('continueSwiping', langCode)}
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
